import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Mail, Lock, User, Chrome, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import api from '@/utils/api';
import { toast } from 'sonner';

const TORTLAB_LOGO = "https://customer-assets.emergentagent.com/job_35f2eb62-f2f6-4b48-9388-e2c1ee261a61/artifacts/tfwz2zen_generated-image-1%20%284%29.png";

export default function Register() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
  });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await api.post('/auth/register', formData);
      localStorage.setItem('auth_token', response.data.token);
      toast.success('Account created successfully!');
      navigate('/dashboard', { state: { user: response.data } });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-black flex flex-col">
      {/* Header */}
      <header className="p-6">
        <Link to="/" className="inline-flex items-center gap-2 text-zinc-400 hover:text-white transition-colors">
          <ArrowLeft className="w-4 h-4" />
          <span className="text-sm">Back to home</span>
        </Link>
      </header>

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center px-6 pb-12">
        <div className="w-full max-w-md space-y-8">
          {/* Logo & Title */}
          <div className="text-center">
            <Link to="/" className="inline-flex items-center gap-3 mb-8">
              <img 
                src={TORTLAB_LOGO} 
                alt="TortLab" 
                className="h-10 w-10 object-contain"
                data-testid="register-logo"
              />
              <span className="font-['Outfit'] font-bold text-2xl text-white">TortLab</span>
            </Link>
            <h1 className="font-['Outfit'] text-4xl font-bold text-white mb-2" data-testid="register-title">
              Create account
            </h1>
            <p className="text-zinc-400">Start deploying AI agents today</p>
          </div>

          {/* Register Form */}
          <div className="bg-zinc-900/50 border border-zinc-800 p-8" data-testid="register-card">
            <form onSubmit={handleRegister} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="name" className="text-sm font-medium text-zinc-300">Full Name</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    placeholder="John Doe"
                    value={formData.name}
                    onChange={handleChange}
                    className="pl-10 h-12 bg-black border-zinc-800 text-white placeholder:text-zinc-600 focus:border-zinc-600 focus:ring-0 rounded-none"
                    required
                    data-testid="register-name-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email" className="text-sm font-medium text-zinc-300">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    placeholder="you@company.com"
                    value={formData.email}
                    onChange={handleChange}
                    className="pl-10 h-12 bg-black border-zinc-800 text-white placeholder:text-zinc-600 focus:border-zinc-600 focus:ring-0 rounded-none"
                    required
                    data-testid="register-email-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-sm font-medium text-zinc-300">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                  <Input
                    id="password"
                    name="password"
                    type="password"
                    placeholder="Create a password"
                    value={formData.password}
                    onChange={handleChange}
                    className="pl-10 h-12 bg-black border-zinc-800 text-white placeholder:text-zinc-600 focus:border-zinc-600 focus:ring-0 rounded-none"
                    required
                    data-testid="register-password-input"
                  />
                </div>
              </div>

              <Button
                type="submit"
                className="w-full h-12 bg-white text-black hover:bg-zinc-200 rounded-none font-semibold text-sm uppercase tracking-wide transition-all"
                disabled={loading}
                data-testid="register-submit-button"
              >
                {loading ? 'Creating account...' : 'Create account'}
              </Button>
            </form>

            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-zinc-800"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="bg-zinc-900/50 px-4 text-zinc-500">or continue with</span>
              </div>
            </div>

            <Button
              type="button"
              variant="outline"
              className="w-full h-12 bg-transparent border border-zinc-800 text-white hover:bg-zinc-800 hover:border-zinc-700 rounded-none transition-all"
              onClick={handleGoogleLogin}
              data-testid="register-google-button"
            >
              <Chrome className="w-5 h-5 mr-2" />
              Google
            </Button>

            <p className="text-center text-sm text-zinc-500 mt-6">
              Already have an account?{' '}
              <Link to="/login" className="text-white font-medium hover:underline" data-testid="register-login-link">
                Sign in
              </Link>
            </p>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="p-6 text-center">
        <p className="text-sm text-zinc-600">
          © {new Date().getFullYear()} TortLab. All rights reserved.
        </p>
      </footer>
    </div>
  );
}
